// src/components/HospitalRoaster/RICLongShortRoster.js
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";

const BASE_URL = "https://ricgop.online/backend";
const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const formatMonthValue = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}`; // "2025-08"
const formatMonthLabel = (date) =>
  date.toLocaleString("default", { month: "long", year: "numeric" }); // "August 2025"

export default function RICLongShortRoster() {
  const [rows, setRows] = useState({});
  const [availableMonths, setAvailableMonths] = useState([]);
  const [month, setMonth] = useState(
    formatMonthValue(new Date())  // "2025-08"
  );
  const [selectedMonth, setSelectedMonth] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeCell, setActiveCell] = useState(null);
  const inputRefs = useRef({});


  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [mode, setMode] = useState(""); // "", "generate", "load"

 

// ✅ put this INSIDE component
const loadRosterForMonth = async (monthValue) => {
  try {
    const res = await axios.get(`${BASE_URL}/long_short_roster.php`, {
      params: { fn: "load", month: monthValue }, // ✅ always YYYY-MM
    });

    if (res.data && res.data.rows) {
      const data = {};
      res.data.rows.forEach((r) => {
        const d = new Date(r.date);
        const iso = d.toISOString().split("T")[0];

        // ✅ Only keep Wednesdays for the table
        if (d.getDay() === 3) { // 0=Sun, 1=Mon, 2=Tue, 3=Wed...
          data[iso] = {
            Presenter: r.presenter || "",
            Supervisor: r.supervisor || "",
          };
        }
      });

      setRows(data);  // table will now only have Wednesday rows
      setMonth(monthValue);
    }
  } catch (err) {
    Swal.fire("Error", "Failed to load roster.", "error");
  }
};




  // Default one row
  useEffect(() => {
    setRows((prev) => {
      if (Object.keys(prev).length) return prev;
      const today = new Date();
      const iso = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(
        today.getDate()
      )}`;
      return { [iso]: { Presenter: "", Supervisor: "" } };
    });
  }, []);

 // Generate fallback 18 months (last 6 + current + next 12)
const generateMonthList = () => {
  const months = [];
  const now = new Date();
  const currentMonthIndex = now.getMonth();
  const currentYear = now.getFullYear();

  // Previous 6 months
  for (let i = 6; i > 0; i--) {
    const d = new Date(currentYear, currentMonthIndex - i, 1);
    months.push({ value: formatMonthValue(d), label: formatMonthLabel(d) });
  }

  // Current + next 12 months
  for (let i = 0; i <= 12; i++) {
    const d = new Date(currentYear, currentMonthIndex + i, 1);
    months.push({ value: formatMonthValue(d), label: formatMonthLabel(d) });
  }

  return months;
};

useEffect(() => {
  const fallback = generateMonthList();

  axios
    .get(`${BASE_URL}/long_short_roster.php`, { params: { fn: "list" } })
    .then((res) => {
      let merged = [...fallback];

      if (res.data && Array.isArray(res.data.months)) {
  const backendMonths = res.data.months.map((m) => {
    let value, label;

    if (/^\d{4}-\d{2}$/.test(m)) {
      value = m;
      const [y, mm] = m.split("-");
      label = formatMonthLabel(new Date(y, mm - 1, 1));
    } else {
      const d = new Date(m + " 01");
      value = !isNaN(d) ? formatMonthValue(d) : m;
      label = !isNaN(d) ? m : m;
    }

    return { value, label, fromBackend: true }; // ✅ mark as backend
  });

  // keep merged list for save dropdown
  const merged = [
    ...backendMonths,
    ...fallback.filter((f) => !backendMonths.some((b) => b.value === f.value)),
  ];

  setAvailableMonths(merged);
}

      // Auto-load current month
    //   const currentValue = formatMonthValue(new Date()); // "2025-08"
    //   const currentMonth = merged.find((m) => m.value === currentValue);
    //   if (currentMonth) {
    //     setSelectedMonth(currentMonth.value);
    //     loadRosterForMonth(currentMonth.value);
    //   }
    })
    .catch(() => {
      setAvailableMonths(fallback);
      const currentValue = formatMonthValue(new Date());
      setSelectedMonth(currentValue);
      loadRosterForMonth(currentValue);
    });
}, []);




// ✅ Load roster data from backend for given month range
const loadRosterByRange = async () => {
  if (!fromDate || !toDate) {
    Swal.fire("⚠️ Error", "Please select both From and To months.", "warning");
    return;
  }

  // expand months to full date range
  const start = new Date(fromDate + "-01"); // first day of start month
  const end = new Date(toDate + "-01");
  end.setMonth(end.getMonth() + 1, 0); // last day of end month

  try {
    const res = await axios.get(`${BASE_URL}/long_short_roster.php`, {
      params: {
        fn: "load_range",
        from: start.toISOString().split("T")[0],
        to: end.toISOString().split("T")[0],
      },
    });

    if (res.data && res.data.rows) {
      const data = {};
      res.data.rows.forEach((r) => {
        const iso = new Date(r.date).toISOString().split("T")[0];
        data[iso] = {
          Presenter: r.presenter || "",
          Supervisor: r.supervisor || "",
        };
      });
      setRows(data);
      Swal.fire("✅ Loaded", "Roster data loaded successfully.", "success");
    } else {
      Swal.fire("ℹ️ Info", "No data found for this range.", "info");
    }
  } catch (err) {
    console.error(err);
    Swal.fire("❌ Error", "Failed to load roster data.", "error");
  }
  setMode("load");
};

// ✅ Generate only the last Wednesday of each month in range & merge DB data
const generateWednesdays = async () => {
  if (!fromDate || !toDate) {
    Swal.fire("⚠️ Error", "Please select both From and To months.", "warning");
    return;
  }

  // expand months to full date range
  const start = new Date(fromDate + "-01");
  const end = new Date(toDate + "-01");
  end.setMonth(end.getMonth() + 1, 0); // last day of end month

  if (start > end) {
    Swal.fire("⚠️ Error", "From month cannot be after To month.", "warning");
    return;
  }

  const newRows = {};
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);

  while (cursor <= end) {
    // ✅ Find last day of this month
    const lastDayOfMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);

    // ✅ Walk backwards to find last Wednesday
    let lastWednesday = new Date(lastDayOfMonth);
    while (lastWednesday.getDay() !== 3) { // Wednesday = 3
      lastWednesday.setDate(lastWednesday.getDate() - 1);
    }

    // ✅ Add only if it's within selected range
    if (lastWednesday >= start && lastWednesday <= end) {
      const iso = `${lastWednesday.getFullYear()}-${pad(
        lastWednesday.getMonth() + 1
      )}-${pad(lastWednesday.getDate())}`;
      newRows[iso] = { Presenter: "", Supervisor: "" };
    }

    // ✅ Move to next month
    cursor.setMonth(cursor.getMonth() + 1);
    cursor.setDate(1);
  }

  if (Object.keys(newRows).length === 0) {
    Swal.fire("ℹ️ Info", "No last Wednesdays found in selected range.", "info");
    return;
  }

  try {
    // 🔥 Fetch DB data for the same expanded range
    const res = await axios.get(`${BASE_URL}/long_short_roster.php`, {
      params: {
        fn: "load_range",
        from: start.toISOString().split("T")[0],
        to: end.toISOString().split("T")[0],
      },
    });

    if (res.data.success && res.data.rows) {
      res.data.rows.forEach((r) => {
        if (newRows[r.date]) {
          newRows[r.date] = {
            Presenter: r.presenter || "",
            Supervisor: r.supervisor || "",
          };
        }
      });
    }
  } catch (err) {
    console.error("Load existing data failed:", err);
  }

  setRows(newRows);
  setMode("generate");
};






// ✅ Fix: do not remove spaces when normalizing
const normalizeValue = (raw) => raw.replace(/\s{2,}/g, " ");


const updateValue = (dateISO, field, rawValue) => {
const value = normalizeValue(rawValue);
setRows((prev) => ({
...prev,
[dateISO]: { ...prev[dateISO], [field]: value },
}));


const lastToken = value.split(/[,]+/).pop().trim();
if (lastToken.length >= 1) {
axios
.get(`${BASE_URL}/get_names.php`, { params: { name: lastToken } })
.then((res) => setSuggestions(Array.isArray(res.data) ? res.data : []))
.catch(() => setSuggestions([]));
} else {
setSuggestions([]);
}


setHighlightedIndex(-1);
setActiveCell({ dateISO, field });
};

   const handleKeyDown = (e, dateISO, field) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((p) => (p + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((p) => (p <= 0 ? suggestions.length - 1 : p - 1));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      insertSuggestion(suggestions[highlightedIndex], dateISO, field);
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setHighlightedIndex(-1);
    }
  };

  const insertSuggestion = (name, dateISO, field) => {
    const input = inputRefs.current[`${dateISO}-${field}`];
    const currentText = rows[dateISO]?.[field] || "";
    const cursor = input?.selectionStart || currentText.length;
    const match = currentText.slice(0, cursor).match(/[\w\s]*$/);
    const insertStart = match ? cursor - (match[0]?.length || 0) : cursor;
    const textBefore = currentText.slice(0, insertStart);
    const textAfter = currentText.slice(cursor);
    const separator =
      textBefore.trim().endsWith(",") || textBefore.trim() === ""
        ? ""
        : ", ";
    const newText = (
      textBefore +
      separator +
      name +
      textAfter
    ).replace(/,\s*/g, ", ");
    updateValue(dateISO, field, newText);
    setTimeout(() => input?.focus(), 0);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };
  const updateDate = (oldISO, newDate) => {
    if (!newDate) return;
    const d = new Date(newDate);
    const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}`;
    setRows((prev) => {
      const copy = { ...prev };
      copy[iso] = { ...copy[oldISO] };
      delete copy[oldISO];
      return copy;
    });
  };

// Save roster
const saveRoster = async () => {
  try {
    const rowsArray = Object.entries(rows).map(([date, vals]) => ({
      date,
      presenter: vals["Presenter"]?.trim() || "",
      supervisor: vals["Supervisor"]?.trim() || "",
    }));

    // validation
    for (let r of rowsArray) {
      if (!r.presenter || !r.supervisor) {
        Swal.fire("⚠️ Validation Error", "All rows must have both Presenter and Supervisor.", "warning");
        return;
      }
    }

    // ✅ make sure we call save_range, not save
    await axios.post(`${BASE_URL}/long_short_roster.php`, {
      fn: "save_range",
      from: fromDate,
      to: toDate,
      rows: rowsArray,
    });

    Swal.fire("✅ Success", "Roster saved successfully.", "success");
  } catch (err) {
    Swal.fire("❌ Error", "Failed to save roster.", "error");
  }
};




  // Load roster manually
  const loadRoster = async () => {
    if (!selectedMonth) return;
    await loadRosterForMonth(selectedMonth);
  };

// ---- inside RICLongShortRoster component ----

// Helper: auto translate to Urdu
const autoTranslateToUrdu = async (text) => {
  if (!text?.trim()) return text;
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(
        text
      )}`
    );
    const json = await res.json();
    return json[0]?.map((item) => item[0]).join("") || text;
  } catch (err) {
    console.error("Translation error:", err);
    return text;
  }
};

// Fancy PDF Generator
// Fancy PDF Generator (download or return Blob)
const generateFancyPDF = async ({ language = "en", month, rows, download = true }) => {
  const isUrdu = language === "ur";

  const urduHeaders = {
    Date: "تاریخ",
    Presenter: "پریزنٹر",
    Supervisor: "سپر وائزر"
  };

  const hospitalName = isUrdu
   ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی"
  : "Rawalpindi Institute of Cardiology";
  const subAddress = isUrdu
    ? "راول روڈ، راولپنڈی"
    : "Rawal Road, Rawalpindi";

  const title = isUrdu
    ? "لانگ/شارٹ کیس روسٹر"
    : "Long/Short Case Roster";

  const subTitle = isUrdu
    ? "ہر مہینے کے آخری بدھ کا روسٹر"
    : "Roster of Last Wednesday of Each Month";

  const leftLogoUrl = "../../assets/img/logo/new-logo.png";
  const rightLogoUrl = "../../assets/img/logo/punjab-logo.png";

  // Build headers
  const headers = ["Date", "Presenter", "Supervisor"]
    .map(
      (h) =>
        `<th style="padding: 10px;">${isUrdu ? urduHeaders[h] || h : h}</th>`
    )
    .join("");

  // Build rows with Urdu translation if needed
  const bodyRows = await Promise.all(
    rows.map(async (r, index) => {
      const bg = index % 2 === 0 ? "#f4f6fb" : "#ffffff";
      const date = r.date;
      const presenter = isUrdu
        ? await autoTranslateToUrdu(r.presenter || "")
        : r.presenter || "";
      const supervisor = isUrdu
        ? await autoTranslateToUrdu(r.supervisor || "")
        : r.supervisor || "";

      return `<tr style="background-color:${bg};">
                <td>${date}</td>
                <td>${presenter}</td>
                <td>${supervisor}</td>
              </tr>`;
    })
  );

  const content = `
 <div style="font-family: ${
      isUrdu ? "'Noto Nastaliq Urdu'" : "'Segoe UI', sans-serif"
    };
    direction: ${isUrdu ? "rtl" : "ltr"};
    padding: 20px; font-size: 13px; color: #2c3e50;">

  <!-- Header -->
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
    
    <!-- Left Logo -->
    <div style="width: 25%; display: flex; justify-content: center;">
      <img src="${leftLogoUrl}" style="max-height: 100px; width: auto; object-fit: contain;" />
    </div>

    <!-- Center Text -->
    <div style="text-align: center; width: 50%;">
      <h2 style="margin: 0; font-weight: 700;">${hospitalName}</h2>
      <p style="margin: 2px 0; font-size: 13px; color: #555;">${subAddress}</p>
      <h4 style="margin: 6px 0; color: #444;">${title} – ${month}</h4>
      <p style="margin: 2px 0; font-size: 14px; font-weight: 600; color: #333;">
        ${subTitle}
      </p>
    </div>

    <!-- Right Logo -->
    <div style="width: 25%; display: flex; justify-content: center;">
      <img src="${rightLogoUrl}" style="max-height: 100px; width: auto; object-fit: contain;" />
    </div>
  </div>
</div>




      <!-- Table -->
      <table style="width: 100%; border-collapse: collapse; font-size: 12px; text-align: center;">
        <thead>
          <tr style="background: linear-gradient(to right, #0052D4, #4364F7); color: #fff;">
            ${headers}
          </tr>
        </thead>
        <tbody>
          ${bodyRows.join("")}
        </tbody>
      </table>

      
<div style="margin-top: 80px; display: flex; justify-content: center; gap: 80px; font-size: 13px;">
  <!-- Left Signature -->
  <div style="text-align: center; line-height: 1.5;">
    <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
    <strong>Dr. Asma Shabbir</strong><br />
    <span style="${isUrdu ? 'direction: rtl; display: block;' : ''}">
      ${isUrdu ? "اسسٹنٹ پروفیسر کارڈیالوجی" : "Assistant Professor of Cardiology"}
    </span><br />
    <span style="${isUrdu ? 'direction: rtl; display: block;' : ''}">
      ${hospitalName}
    </span>
  </div>

  <!-- Right Signature -->
  <div style="text-align: center; line-height: 1.5;">
    <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
    <strong>Dr. Asim Javed</strong><br />
    <span style="${isUrdu ? 'direction: rtl; display: block;' : ''}">
      ${isUrdu ? "ایسوسی ایٹ پروفیسر / ایچ او ڈی کارڈیالوجی" : "Associate Professor / HOD Cardiology"}
    </span><br />
    <span style="${isUrdu ? 'direction: rtl; display: block;' : ''}">
      ${hospitalName}
    </span>
  </div>
</div>




      <!-- Footer -->
      <p style="text-align:center; margin-top: 40px; font-size: 10px; color: #888;">
        © ${new Date().getFullYear()} ${hospitalName}. All rights reserved.
      </p>
    </div>
  `;

  // Generate with html2pdf
  const opt = {
    margin: 0.5,
    filename: `LongShort_Roster_${month}_${language.toUpperCase()}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
  };

  if (download) {
    // ✅ Download for user
    await html2pdf().from(content).set(opt).save();
    return null;
  } else {
    // ✅ Return Blob for email
    return await html2pdf().from(content).set(opt).outputPdf("blob");
  }
};


// Generate Both PDFs on One Click
const generateBothPDFs = async () => {
  const rowsArray = Object.entries(rows).map(([date, vals]) => ({
    date,
    presenter: vals.Presenter,
    supervisor: vals.Supervisor,
  }));

  await generateFancyPDF({ language: "en", month, rows: rowsArray, download: true });
  await generateFancyPDF({ language: "ur", month, rows: rowsArray, download: true });
};


// Email Long/Short Roster
const emailRoster = async () => {
  try {
    Swal.fire({ title: "Preparing PDFs...", html: "Please wait...", showConfirmButton: false });

    const rowsArray = Object.entries(rows).map(([date, vals]) => ({
      date,
      presenter: vals.Presenter,
      supervisor: vals.Supervisor,
    }));

    // ✅ Get blobs instead of download
    const enBlob = await generateFancyPDF({ language: "en", month, rows: rowsArray, download: false });
    const urBlob = await generateFancyPDF({ language: "ur", month, rows: rowsArray, download: false });

    const formData = new FormData();
    formData.append("englishPDF", new File([enBlob], `LongShort_Roster_${month}_EN.pdf`, { type: "application/pdf" }));
    formData.append("urduPDF", new File([urBlob], `LongShort_Roster_${month}_UR.pdf`, { type: "application/pdf" }));
    formData.append("month", month);

    await axios.post("https://ricgop.online/backend/email_long_short_roster.php", formData);

    Swal.fire("📧 Emailed", "Long/Short Roster sent successfully!", "success");
  } catch (err) {
    console.error(err);
    Swal.fire("❌ Error", "Failed to send Long/Short Roster email.", "error");
  }
};


// const generatePDFBlob = async ({ language, month, rows }) => {
//   const { jsPDF } = await import("jspdf");
//   const doc = new jsPDF();

//   doc.setFont("helvetica", "normal");
//   doc.setFontSize(12);

//   doc.text(`Long/Short Roster - ${month} (${language})`, 10, 10);

//   if (Array.isArray(rows) && rows.length > 0) {
//     rows.forEach((row, idx) => {
//       const line = `${row.date || "N/A"} - ${row.presenter || "N/A"} / ${row.supervisor || "N/A"}`;
//       const split = doc.splitTextToSize(line, 180); // wrap long text
//       doc.text(split, 10, 20 + idx * 10);
//     });
//   } else {
//     const split = doc.splitTextToSize("⚠️ No roster data available", 180);
//     doc.text(split, 10, 20);
//   }

//   return new Blob([doc.output("arraybuffer")], { type: "application/pdf" });
// };



  const addRow = () => {
    const today = new Date();
    const iso = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(
      today.getDate()
    )}-${Date.now()}`;
    setRows((prev) => ({
      ...prev,
      [iso]: { Presenter: "", Supervisor: "" },
    }));
  };
  

  return (
      <div className="container bg-light p-4 rounded-4 shadow border">
      <h4 className="text-center mb-4">🩺 Long/Short Case Roster</h4>
      <h5 className="text-center mb-4">Roster of Last Wednesday of Each Month</h5>

     {/* Month Range Picker */}
<div className="d-flex flex-wrap justify-content-center align-items-end gap-3 mb-4">
  <div>
    <label className="fw-semibold">From Month:</label>
    <input
      type="month"
      className="form-control"
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
    />
  </div>
  <div>
    <label className="fw-semibold">To Month:</label>
    <input
      type="month"
      className="form-control"
      value={toDate}
      onChange={(e) => setToDate(e.target.value)}
    />
  </div>
  <button className="btn btn-primary" onClick={generateWednesdays}>
    🔄 Generate Wednesdays
  </button>
  <button className="btn btn-warning" onClick={loadRosterByRange}>
    📂 Load Data
  </button>
</div>


      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "140px" }}>Date</th>
              <th>Presenter</th>
              <th>Supervisor</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(rows).map((iso) => (
              <tr key={iso}>
                <td>
                  <input
                    type="date"
                    className="form-control"
                    value={iso}
                    disabled
                  />
                </td>
                {["Presenter", "Supervisor"].map((field) => (
                  <td key={field} style={{ position: "relative" }}>
                    <input
                      className="form-control"
                      placeholder={
                        field === "Presenter"
                          ? "e.g., Dr Sibtain, Dr Raza"
                          : "e.g., Dr Hamza, Dr Sumera"
                      }
                      value={rows[iso]?.[field] || ""}
                      onChange={(e) => updateValue(iso, field, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, iso, field)}
                      ref={(el) =>
                        (inputRefs.current[`${iso}-${field}`] = el)
                      }
                    />
                    {activeCell?.dateISO === iso &&
                      activeCell?.field === field &&
                      suggestions.length > 0 && (
                        <ul
                          className="list-group position-absolute w-100 shadow-sm"
                          style={{
                            top: "100%",
                            left: 0,
                            maxHeight: 200,
                            overflowY: "auto",
                            zIndex: 9999,
                          }}
                        >
                          {suggestions.map((s, i) => (
                            <li
                              key={i}
                              className={`list-group-item list-group-item-action ${
                                i === highlightedIndex ? "active" : ""
                              }`}
                              onMouseDown={() =>
                                insertSuggestion(s, iso, field)
                              }
                            >
                              {s}
                            </li>
                          ))}
                        </ul>
                      )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

  {/* Action Buttons */}
  <div className="my-3 d-flex flex-wrap gap-2 justify-content-center">
  {mode === "generate" && (
    <button className="btn btn-success" onClick={saveRoster}>
      💾 Save
    </button>
  )}

  {mode === "load" && (
    <>
      <button className="btn btn-success" onClick={saveRoster}>
        💾 Save
      </button>
      <button className="btn btn-primary" onClick={generateBothPDFs}>
        📄 Download EN + UR PDFs
      </button>
      <button className="btn btn-warning" onClick={emailRoster}>
        📧 Email
      </button>
    </>
  )}
</div>

</div>

  );
}
